import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * 企業の value match 指標（企業 LP のバリューマッチ表示用）。
 */
export interface CompanyValueMatchItem {
  label: string;
  score: number;
}

/**
 * 企業マスタのドメイン型。admin-console-api の companies テーブル準拠（17 編集可能フィールド）。
 */
export interface Company {
  id: number;
  name: string;
  corporateUrl: string | null;
  logoUrl: string | null;
  address: string | null;
  employees: string | null;
  established: string | null;
  business: string | null;
  capital: string | null;
  ceo: string | null;
  topViewTitle: string | null;
  topViewSubTitle: string | null;
  topViewTag: string | null;
  aboutContents: string | null;
  content: string | null;
  officeLocation: string | null;
  ceoPictureUrl: string | null;
  valueMatch: CompanyValueMatchItem[];
  createdAt: string;
  updatedAt: string;
}

/**
 * 企業一覧クエリのパラメータ型。
 * API は page / pageSize / name（部分一致）を受け取る。
 */
export interface CompanyListParams {
  page: number;
  pageSize: number;
  name?: string;
  sortBy?: 'name' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * GET /api/v1/companies のレスポンス型。
 */
export interface CompanyListResponse {
  items: Company[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * GET・POST・PUT /api/v1/companies(/:companyId) のレスポンス型。
 */
export interface CompanyDetailResponse {
  company: Company;
}

/**
 * 企業作成・更新フォームの入力型。
 */
export interface CompanyInput {
  name: string;
  corporateUrl: string;
  logoUrl: string;
  address: string;
  employees: string;
  established: string;
  business: string;
  capital: string;
  ceo: string;
  topViewTitle: string;
  topViewSubTitle: string;
  topViewTag: string;
  aboutContents: string;
  content: string;
  officeLocation: string;
  ceoPictureUrl: string;
  valueMatch: CompanyValueMatchItem[];
}

const BASE = '/companies';

/**
 * 企業マスタ用 queryOptions factory。
 * `useQuery` / `ensureQueryData` / `prefetchQuery` で使い回す。
 */
export const companyQueries = {
  all: ['companies'] as const,
  list: (params: CompanyListParams) =>
    queryOptions({
      queryKey: ['companies', 'list', params] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<CompanyListResponse>(BASE, {
          page: params.page,
          pageSize: params.pageSize,
          name: params.name,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        });
        return data;
      },
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['companies', 'detail', id] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<CompanyDetailResponse>(`${BASE}/${id}`);
        return data;
      },
    }),
};

/**
 * 企業作成。
 */
export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompanyInput) => {
      const { data } = await apiClient.post<CompanyDetailResponse>(BASE, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyQueries.all });
    },
  });
}

/**
 * 企業更新。
 */
export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: number } & CompanyInput) => {
      const { data } = await apiClient.put<CompanyDetailResponse>(`${BASE}/${id}`, input);
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: companyQueries.all });
      qc.invalidateQueries({ queryKey: ['companies', 'detail', String(vars.id)] });
    },
  });
}

/**
 * 企業削除。
 */
export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`${BASE}/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyQueries.all });
    },
  });
}
